import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Users, FileText, AlertCircle, Shield, Settings, CreditCard, Building2, MessageSquare, User, Crown, Calculator, BarChart3, UserCheck, Search, UserPlus, Receipt, Video, Home, HardHat, Tag, Mail, Scale, FolderOpen, MapPin, AlertTriangle, Eraser, Send, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useAdminDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [coopers, setCoopers] = useState([]);
  const [_dispositifs, setDispositifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'coopers');

  useEffect(() => {
    if ((searchParams.get('tab') || 'coopers') !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, coopersRes, dispositifsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/coopers`, { headers: getAuthHeader() }),
        axios.get(`${API}/dispositifs`, { headers: getAuthHeader() })
      ]);

      setStats(statsRes.data);
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setCoopers(sortByDate(coopersRes.data?.coopers || coopersRes.data || []));
      setDispositifs(sortByDate(dispositifsRes.data));
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCooper = async (cooperId) => {
    try {
      await axios.put(`${API}/coopers/${cooperId}/validate`, {}, { headers: getAuthHeader() });
      toast.success('COOPER\'S validé avec succès !');
      fetchData();
    } catch {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const sidebarTabs = [
    { value: 'simulateur', label: 'Garantie RCDS-BATICO', icon: <Calculator className="h-5 w-5" />, highlight: true },
    { value: 'simulateurs-separator', label: '🎯 SIMULATEURS MAÎTRES', icon: null, isSeparator: true, isHighlight: true },
    { value: 'simulateur-dos-v12', label: 'DOS-DOMIUM', icon: <Home className="h-5 w-5 text-orange-500" /> },
    { value: 'suivi-dos-domium', label: 'Suivi DOS DOMIUM', icon: <Building2 className="h-5 w-5 text-violet-600" /> },
    { value: 'ccmi-cockpit', label: 'Cockpit CCMI', icon: <HardHat className="h-5 w-5 text-indigo-600" />, isLink: true, path: '/admin/ccmi-dashboard', highlight: true },
    { value: 'vefa-tunnel', label: 'VEFA — Programme immobilier', icon: <Building2 className="h-5 w-5 text-amber-700" />, isLink: true, path: '/simulateurs/vefa-tunnel', highlight: true },
    { value: 'declaration-dommage', label: 'Déclaration Dommage', icon: <AlertTriangle className="h-5 w-5 text-red-500" />, highlight: true },
    { value: 'gestion', label: 'Gestion Complète', icon: <Settings className="h-5 w-5" /> },
    { value: 'statistiques', label: 'Statistiques par Période', icon: <BarChart3 className="h-5 w-5 text-purple-600" /> },
    { value: 'ftpe-stats', label: 'Tableau de bord FTPE', icon: <Shield className="h-5 w-5 text-blue-600" />, highlight: true },
    { value: 'messagerie', label: 'Messagerie', icon: <MessageSquare className="h-5 w-5" /> },
    ...(isSuperAdmin ? [
      { value: 'super-dashboard', label: '🛡️ Tableau de bord Super Admin', icon: <Crown className="h-5 w-5 text-yellow-500" />, isLink: true, path: '/admin/super-dashboard' },
      { value: 'gestion-admins', label: 'Gestion des Admins', icon: <Shield className="h-5 w-5 text-purple-600" /> },
      { value: 'frontoffice-plans', label: '🏷️ Plans Vitrine', icon: <Tag className="h-5 w-5 text-amber-700" />, highlight: true },
      { value: 'organizations', label: '🏢 Organisations', icon: <Building2 className="h-5 w-5 text-indigo-700" />, highlight: true },
      { value: 'newsletter', label: '📨 Newsletter', icon: <Mail className="h-5 w-5 text-emerald-700" />, highlight: true },
      { value: 'legalcoop-analytics', label: '💼 LEGALCOOP Analytics', icon: <BarChart3 className="h-5 w-5 text-blue-700" />, highlight: true },
      { value: 'juriste-dashboard', label: '⚖️ Dashboard Juriste', icon: <Scale className="h-5 w-5 text-red-700" />, highlight: true },
      { value: 'communityplace-admin', label: '🏛️ Communityplace', icon: <Settings className="h-5 w-5 text-orange-700" />, highlight: true },
      { value: 'cap-bati-attestations', label: '✅ Attestations CAP BATI+ CMI', icon: <Settings className="h-5 w-5 text-emerald-700" />, highlight: true },
      { value: 'cap-bati-plus-cmi', label: '🏗️ CAP BATI+ CMI', icon: <Settings className="h-5 w-5 text-orange-700" />, highlight: true },
    ] : []),
    { value: 'gestion-membres-cooper', label: 'Gestion Membres/COOPER\'S', icon: <Users className="h-5 w-5" /> },
    { value: 'mes-membres-admin', label: 'Mes Membres', icon: <UserPlus className="h-5 w-5 text-blue-600" />, highlight: true },
    { value: 'achats-attente', label: 'Achats en attente', icon: <CreditCard className="h-5 w-5" /> },
    { value: 'coopers', label: 'COOPER\'S', icon: <Shield className="h-5 w-5" /> },
    { value: 'adhesions', label: 'Gestion des Adhésions', icon: <UserCheck className="h-5 w-5" /> },
    { value: 'recherche', label: 'Recherche Unifiée', icon: <Search className="h-5 w-5" /> },
    { value: 'experts-validation', label: "Validation COOPER'S Advisors", icon: <Shield className="h-5 w-5" /> },
    { value: 'finance-separator', label: '💰 GESTION FINANCIÈRE', icon: null, isSeparator: true, isHighlight: true },
    { value: 'souscriptions-validation', label: '⚡ Validation Paiements', icon: <AlertCircle className="h-5 w-5 text-amber-500" />, isFinance: true },
    { value: 'sepa-rcd', label: '🏦 Mandats SEPA/CB', icon: <CreditCard className="h-5 w-5 text-[#DEA900]" />, isFinance: true },
    { value: 'virements-ess', label: '💳 Factures & Reçus ESS', icon: <Receipt className="h-5 w-5 text-emerald-500" />, isFinance: true },
    { value: 'souscriptions-liste', label: '📋 Toutes Souscriptions', icon: <FileText className="h-5 w-5 text-blue-500" />, isFinance: true },
    { value: 'suivi-signatures', label: '✍️ Suivi des signatures', icon: <FileText className="h-5 w-5 text-purple-600" /> },
    ...(isSuperAdmin ? [
      { value: 'ged', label: 'GED', icon: <FolderOpen className="h-5 w-5" /> },
      { value: 'nettoyage-zero', label: 'Devis à 0 €', icon: <Eraser className="h-5 w-5 text-rose-600" /> },
      { value: 'activites-textes', label: 'Définitions activités', icon: <HardHat className="h-5 w-5 text-amber-600" /> },
      { value: 'envois-attestations-mo', label: 'Attestations aux MO', icon: <Send className="h-5 w-5 text-sky-700" /> },
      { value: 'derogations-ftpe', label: 'Dérogations adhésion', icon: <EyeOff className="h-5 w-5 text-amber-700" /> },
    ] : []),
    { value: 'videos', label: 'Vidéos', icon: <Video className="h-5 w-5 text-purple-600" /> },
    { value: 'territoires', label: 'Territoires', icon: <MapPin className="h-5 w-5" /> },
    { value: 'profil', label: 'Mon Profil', icon: <User className="h-5 w-5" /> },
    { value: 'config', label: 'Configuration', icon: <Settings className="h-5 w-5" /> },
  ].filter((tab) => {
    // Rôle personnalisé : seuls les menus choisis par le Super Admin (et « Mon Profil ») restent visibles.
    if (isSuperAdmin || !user?.custom_role || !Array.isArray(user.custom_role_menus)) return true;
    if (tab.value.endsWith('-separator')) return false;
    return tab.value === 'profil' || (user.custom_role_menus || []).includes(tab.value);
  });

  const allowedTabKey = sidebarTabs.map((t) => t.value).join('|');
  useEffect(() => {
    if (!isSuperAdmin && Array.isArray(user?.custom_role_menus) && !allowedTabKey.split('|').includes(activeTab)) {
      setActiveTab(allowedTabKey.split('|')[0] || 'profil');
    }
  }, [isSuperAdmin, user?.custom_role, allowedTabKey, activeTab]);

  return {
    user, getAuthHeader, navigate,
    stats, coopers, loading, activeTab, setActiveTab,
    handleValidateCooper, handleLogout, isSuperAdmin, sidebarTabs,
  };
}
